import { Select, Tag, Text } from "@instructure/ui";
import React, { useEffect, useState } from "react";

const MULTI = "multi";
const SINGLE = "single";

const Selector = ({
  type,
  id,
  label,
  options,
  selectHandler,
  selectedItemId,
  selectedOptions,
  interaction = true,
  size,
  clear,
  assistiveText,
  width,
  placeholder,
  messages,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isShowingOptions, setIsShowingOptions] = useState(false);
  const [highlightedOptionId, setHighlightedOptionId] = useState(null);
  const [selectedOptionId, setSelectedOptionId] = useState(
    selectedItemId ? selectedItemId : null
  );
  const [selectedOptionIds, setSelectedOptionIds] = useState(
    selectedOptions ? selectedOptions : []
  );
  const [filteredOptions, setFilteredOptions] = useState(options);
  const selectorType = type ? type : SINGLE;

  useEffect(() => {
    setFilteredOptions(options);
    if (selectorType === SINGLE) {
      if (selectedItemId) {
        const option = getOptionById(selectedItemId);
        setInputValue(option ? option.label : "");
      }
    } else if (selectorType === MULTI) {
      const optionIds = options.map((o) => o.id);
      if (selectedOptions) {
        const newSelectedOptions = selectedOptions.filter((o) =>
          optionIds.includes(o)
        );
        setSelectedOptionIds(newSelectedOptions);
      }
    }
  }, [options, selectedOptionId, selectedOptions]);

  useEffect(() => {
    setSelectedOptionId(selectedItemId);
  }, [selectedItemId]);

  useEffect(() => {
    if (clear || !interaction) {
      setInputValue("");
      setSelectedOptionId(null);
      setSelectedOptionIds([]);
    }
  }, [clear, interaction]);

  const getOptionById = (queryId) => {
    return options.find(({ id }) => id === queryId);
  };

  const getOptionsByValue = (value) => {
    return options.filter((option) =>
      option.label.toLowerCase().startsWith(value.toLowerCase())
    );
  };

  const matchValue = () => {
    // an option matching user input exists
    if (filteredOptions.length === 1) {
      const onlyOption = filteredOptions[0];
      // automatically select the matching option
      if (onlyOption.label.toLowerCase() === inputValue.toLowerCase()) {
        if (selectorType === SINGLE) {
          setInputValue(onlyOption.label);
          setSelectedOptionId(onlyOption.id);
        } else if (selectorType === MULTI) {
          setInputValue("");
          setSelectedOptionIds([...selectedOptionIds, onlyOption.id]);
        }
        setFilteredOptions(getOptionsByValue(""));
      }
    }
    if (highlightedOptionId) {
      if (inputValue === getOptionById(highlightedOptionId).label) {
        if (selectorType === MULTI) {
          setInputValue("");
        }
        setFilteredOptions(getOptionsByValue(""));
      }
    }
  };

  const handleShowOptions = () => {
    setIsShowingOptions(true);
  };

  const handleHideOptions = () => {
    setIsShowingOptions(false);
    matchValue();
    if (selectorType === SINGLE) {
      setHighlightedOptionId(null);
    }
  };

  const handleBlur = () => {
    setHighlightedOptionId(null);
  };

  const handleHighlightOption = (event, { id }) => {
    event.persist();
    const option = getOptionById(id);
    if (option) {
      setHighlightedOptionId(id);
      setInputValue(event.type === "keydown" ? option.label : inputValue);
    }
  };

  const handleSelectOption = (_e, { id }) => {
    const option = getOptionById(id);
    if (option) {
      setIsShowingOptions(false);
      if (selectorType === SINGLE) {
        if (selectedOptionId !== id) {
          selectHandler(id);
          setSelectedOptionId(id);
        }
        setInputValue(option.label);
        setFilteredOptions(options);
      } else if (selectorType === MULTI) {
        const selectedOptions = [...selectedOptionIds, id];
        setSelectedOptionIds(selectedOptions);
        selectHandler(selectedOptions);
        setHighlightedOptionId(null);
        setFilteredOptions(getOptionsByValue(""));
        setInputValue("");
      }
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    const newOptions = getOptionsByValue(value);
    setInputValue(value);
    setFilteredOptions(newOptions);
    setHighlightedOptionId(newOptions.length > 0 ? newOptions[0].id : null);
    setIsShowingOptions(true);
  };

  const handleKeyDown = (event) => {
    // when backspace key is pressed
    if (event.keyCode === 8) {
      if (inputValue === "" && selectedOptionIds.length > 0) {
        setHighlightedOptionId(null);
        setSelectedOptionIds(selectedOptionIds.slice(0, -1));
      }
    }
  };

  // remove a selected option tag
  const dismissTag = (e, tag) => {
    e.stopPropagation();
    e.preventDefault();

    const newSelection = selectedOptionIds.filter((id) => id !== tag);
    setSelectedOptionIds(newSelection);
    selectHandler(newSelection);
    setHighlightedOptionId(null);
  };

  // render tags when multiple options are selected
  const renderTags = () => {
    return selectedOptionIds.map(
      (id, index) =>
        !!getOptionById(id) && (
          <Tag
            size={size}
            dismissible
            key={id}
            title={`Remove ${getOptionById(id).label}`}
            text={getOptionById(id).label}
            margin={
              index > 0 ? "xxx-small 0 xxx-small xx-small" : "xxx-small 0"
            }
            onClick={(e) => dismissTag(e, id)}
          />
        )
    );
  };

  const handleRenderBeforeInput = () => {
    if (selectedOptionId && !!getOptionById(selectedOptionId)) {
      return getOptionById(selectedOptionId).renderBeforeLabel;
    }
  };

  const handleRenderAfterInput = () => {
    if (selectedOptionId && !!getOptionById(selectedOptionId)) {
      return getOptionById(selectedOptionId).renderAfterLabel;
    }
  };

  return (
    <Select
      id={id}
      renderLabel={label ? <Text size={size}>{label}</Text> : ""}
      assistiveText={assistiveText}
      placeholder={placeholder}
      width={width}
      size={size ?? "medium"}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      isShowingOptions={isShowingOptions}
      onBlur={handleBlur}
      onRequestShowOptions={handleShowOptions}
      onRequestHideOptions={handleHideOptions}
      onRequestHighlightOption={handleHighlightOption}
      onRequestSelectOption={handleSelectOption}
      onKeyDown={selectorType === MULTI ? handleKeyDown : null}
      renderBeforeInput={
        selectorType === MULTI && selectedOptionIds.length > 0
          ? renderTags
          : handleRenderBeforeInput
      }
      renderAfterInput={
        handleRenderAfterInput() ? handleRenderAfterInput : null
      }
      interaction={interaction ? "enabled" : "disabled"}
      messages={messages}
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map(
          (option) =>
            (selectorType === SINGLE ||
              selectedOptionIds.indexOf(option.id) === -1) && (
              <Select.Option
                id={option.id}
                key={option.id}
                isHighlighted={option.id === highlightedOptionId}
                isSelected={
                  selectorType === SINGLE && option.id === selectedOptionId
                }
                renderBeforeLabel={option.renderBeforeLabel}
                renderAfterLabel={option.renderAfterLabel}
              >
                {option.label}
              </Select.Option>
            )
        )
      ) : (
        <Select.Option id="empty-option" key="empty-option">
          ---
        </Select.Option>
      )}
    </Select>
  );
};

export { MULTI, Selector, SINGLE };
